import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { ws, fmtDate, empName } from "./api";
import { Field, NativeSelect, Avatar, Loading } from "./ui";

export const useOrgRefs = () => {
  const [refs, setRefs] = useState({ departments: [], locations: [], employees: [], members: [], loaded: false });
  const load = useCallback(async () => {
    const [d, l, e, m] = await Promise.all([
      ws("/crm/workspace/org/departments"), ws("/crm/workspace/org/locations"), ws("/crm/workspace/employees"), ws("/crm/workspace/members"),
    ]).catch((err) => { toast.error(err.message); return [{ items: [] }, { items: [] }, { employees: [] }, { members: [] }]; });
    setRefs({ departments: d.items, locations: l.items, employees: e.employees, members: m.members, fullAccess: e.full_access, loaded: true });
  }, []);
  useEffect(() => { load(); }, [load]);
  return { ...refs, reload: load };
};

const BLANK = { employee_id: "", first_name: "", last_name: "", email: "", phone: "", designation: "", department_id: null, location_id: null,
  reporting_manager_id: null, joining_date: "", employment_type: "", status: "active", crm_user_id: null };

export const EmployeeDialog = ({ open, onOpenChange, refs, editing, onSaved, prefix, initial }) => {
  const [f, setF] = useState(BLANK);
  useEffect(() => { setF(editing ? { ...BLANK, ...editing } : { ...BLANK, ...(initial || {}) }); }, [editing, open, initial]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const body = { ...f };
    Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
    try {
      if (editing) {
        const { employee_id, org_id, created_at, created_by, updated_at, is_deleted, ...patch } = body;
        await ws(`/crm/workspace/employees/${editing.employee_id}`, { method: "PATCH", body: patch });
        toast.success("Employee updated");
      } else {
        const r = await ws("/crm/workspace/employees", { method: "POST", body });
        toast.success(`Employee ${r.employee_id} created`);
      }
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  const linked = new Set(refs.employees.filter((e) => e.crm_user_id && e.employee_id !== editing?.employee_id).map((e) => e.crm_user_id));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader><DialogTitle>{editing ? `Edit ${editing.employee_id}` : "Add employee"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {!editing && (
            <div className="sm:col-span-2">
              <Field label={`Employee ID (leave blank to auto-generate with prefix ${prefix || "EMP"})`}>
                <Input value={f.employee_id || ""} onChange={(e) => set("employee_id")(e.target.value)} placeholder={`e.g. ${prefix || "EMP"}001`} data-testid="emp-id-input" />
              </Field>
            </div>
          )}
          {[["first_name", "First name"], ["last_name", "Last name"], ["email", "Work email"], ["phone", "Phone"], ["designation", "Designation"], ["employment_type", "Employment type"]].map(([k, l]) => (
            <Field key={k} label={l}><Input value={f[k] || ""} onChange={(e) => set(k)(e.target.value)} data-testid={`emp-${k}-input`} /></Field>
          ))}
          <Field label="Joining date"><Input type="date" value={f.joining_date || ""} onChange={(e) => set("joining_date")(e.target.value)} data-testid="emp-joining_date-input" /></Field>
          <Field label="Status"><NativeSelect value={f.status} onChange={set("status")} options={[{ value: "active", label: "Active" }, { value: "on_notice", label: "On notice" }, { value: "inactive", label: "Inactive" }]} placeholder="Status" testid="emp-status-select" /></Field>
          <Field label="Department"><NativeSelect value={f.department_id} onChange={set("department_id")} options={refs.departments.map((d) => ({ value: d.department_id, label: d.name }))} testid="emp-department-select" /></Field>
          <Field label="Location"><NativeSelect value={f.location_id} onChange={set("location_id")} options={refs.locations.map((d) => ({ value: d.location_id, label: d.name }))} testid="emp-location-select" /></Field>
          <Field label="Reporting manager"><NativeSelect value={f.reporting_manager_id} onChange={set("reporting_manager_id")} options={refs.employees.filter((e) => e.employee_id !== editing?.employee_id).map((e) => ({ value: e.employee_id, label: `${empName(e)} (${e.employee_id})` }))} testid="emp-manager-select" /></Field>
          <Field label="Linked CRM login (optional)"><NativeSelect value={f.crm_user_id} onChange={set("crm_user_id")} options={refs.members.filter((m) => !linked.has(m.crm_user_id)).map((m) => ({ value: m.crm_user_id, label: `${m.name} (${m.email})` }))} placeholder="No login" testid="emp-login-select" /></Field>
        </div>
        <Button disabled={!f.first_name?.trim()} onClick={save} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="emp-save-btn">{editing ? "Save changes" : "Create employee"}</Button>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-50 text-sm"><span className="text-slate-500">{label}</span><span className="text-slate-800 text-right">{value || "—"}</span></div>
);

export const EmployeeDetails = ({ data, refs }) => {
  const e = data.employee;
  const dep = refs.departments.find((d) => d.department_id === e.department_id)?.name;
  const loc = refs.locations.find((d) => d.location_id === e.location_id)?.name;
  return (
    <div data-testid="employee-details">
      <div className="flex items-center gap-4 mb-6">
        <Avatar name={empName(e)} size="h-14 w-14" />
        <div><p className="font-heading text-xl font-semibold text-[#0B1530]" data-testid="employee-details-name">{empName(e)}</p><p className="text-sm text-slate-500">{e.designation || "—"} · <span data-testid="employee-details-id">{e.employee_id}</span></p></div>
      </div>
      <Row label="Department" value={dep} /><Row label="Location" value={loc} /><Row label="Email" value={e.email} />
      {data.full_access && <><Row label="Phone" value={e.phone} /><Row label="Joining date" value={fmtDate(e.joining_date)} /><Row label="Employment type" value={e.employment_type} /><Row label="Status" value={e.status} /></>}
      <div className="mt-6">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Reporting chain</p>
        {data.manager_chain.length === 0 ? <p className="text-sm text-slate-500">No reporting manager set.</p> : (
          <ol className="space-y-1.5" data-testid="manager-chain">{data.manager_chain.map((m, i) => <li key={m.employee_id} className="text-sm" style={{ paddingLeft: i * 12 }}>↑ {empName(m)} <span className="text-slate-400">{m.designation}</span></li>)}</ol>
        )}
      </div>
      <div className="mt-6">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Direct reports ({data.direct_reports.length})</p>
        {data.direct_reports.map((r) => <p key={r.employee_id} className="text-sm">{empName(r)} <span className="text-slate-400">{r.designation}</span></p>)}
      </div>
      {data.history && (
        <div className="mt-6">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Career history</p>
          <ol className="relative border-l border-slate-200 ml-1.5 space-y-4" data-testid="career-history">
            {data.history.map((h, i) => (
              <li key={i} className="pl-4"><span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-[#E8A33D]" />
                <p className="text-sm text-slate-800">{h.field === "joined" ? `Joined — ${h.new}` : `${h.field.replace(/_id$/, "").replace(/_/g, " ")} changed`}</p>
                {h.field !== "joined" && <p className="text-xs text-slate-500">{String(h.old ?? "—")} → {String(h.new ?? "—")}</p>}
                <p className="text-[11px] text-slate-400">{fmtDate(h.changed_at, true)} · by {h.changed_by}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export const EmployeeSheet = ({ employeeId, onClose, refs }) => {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); if (employeeId) ws(`/crm/workspace/employees/${employeeId}`).then(setData).catch((e) => toast.error(e.message)); }, [employeeId]);
  return (
    <Sheet open={!!employeeId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="employee-sheet">
        <SheetHeader><SheetTitle className="sr-only">Employee profile</SheetTitle></SheetHeader>
        {data ? <EmployeeDetails data={data} refs={refs} /> : <Loading />}
      </SheetContent>
    </Sheet>
  );
};
